import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import sbeeLogo from "../../assets/sbeelogo.png";
import { PURCHASE_API, MASTER_API } from "../../apiConfig";

const VENDOR_API = PURCHASE_API.replace("/purchase-request", "");

const styles = {

    wrapper: {
        background: "#e9e9e9",
        minHeight: "100vh",
        padding: "24px",
        boxSizing: "border-box"
    },

    toolbar: {
        maxWidth: "850px",
        margin: "0 auto 12px auto",
        display: "flex",
        justifyContent: "flex-end"
    },

    printBtn: {
        background: "#1d4ed8",
        color: "#fff",
        border: "none",
        borderRadius: "4px",
        padding: "8px 18px",
        fontSize: "14px",
        cursor: "pointer"
    },

    status: {
        maxWidth: "850px",
        margin: "0 auto",
        textAlign: "center",
        padding: "24px",
        fontSize: "14px",
        color: "#444"
    },

    error: {
        color: "#b91c1c"
    },

    slip: {
        background: "#fff",
        maxWidth: "850px",
        margin: "0 auto",
        padding: "28px 32px",
        border: "1px solid #000",
        fontFamily: "Arial, Helvetica, sans-serif",
        color: "#000",
        boxSizing: "border-box"
    },

    header: {
        position: "relative",
        textAlign: "center",
        borderBottom: "1px solid #000",
        paddingBottom: "10px"
    },

    logo: {
        position: "absolute",
        left: 0,
        top: 0,
        height: "48px",
        width: "auto",
        objectFit: "contain"
    },

    titleBlock: {
        textAlign: "center"
    },

    title: {
        fontSize: "18px",
        fontWeight: "bold",
        letterSpacing: "0.5px"
    },

    subtitle: {
        fontSize: "13px",
        fontWeight: "bold",
        marginTop: "4px"
    },

    address: {
        fontSize: "11px",
        fontWeight: "normal",
        marginTop: "4px",
        color: "#333"
    },

    table: {
        width: "100%",
        borderCollapse: "collapse",
        tableLayout: "fixed"
    },

    metaTd: {
        border: "1px solid #000",
        borderTop: "none",
        padding: "6px 8px",
        fontSize: "13px",
        verticalAlign: "middle"
    },

    metaTdFirstRow: {
        border: "1px solid #000",
        padding: "6px 8px",
        fontSize: "13px",
        verticalAlign: "middle"
    },

    metaLabel: {
        fontWeight: "bold",
        width: "18%"
    },

    metaNote: {
        fontWeight: "normal",
        fontSize: "11px"
    },

    instruction: {
        fontWeight: "bold",
        fontSize: "13px",
        padding: "8px 4px",
        borderLeft: "1px solid #000",
        borderRight: "1px solid #000"
    },

    th: {
        border: "1px solid #000",
        padding: "6px 6px",
        fontSize: "12px",
        textAlign: "center",
        fontWeight: "bold",
        background: "#f5f5f5"
    },

    td: {
        border: "1px solid #000",
        padding: "6px 6px",
        fontSize: "12px",
        textAlign: "center",
        height: "26px"
    },

    tdLeft: {
        border: "1px solid #000",
        padding: "6px 6px",
        fontSize: "12px",
        textAlign: "left",
        height: "26px"
    },

    colSl: {
        width: "7%"
    },

    footerTd: {
        border: "1px solid #000",
        borderTop: "none",
        padding: "10px 8px",
        fontSize: "13px"
    },

    footerTdFirstRow: {
        border: "1px solid #000",
        padding: "10px 8px",
        fontSize: "13px"
    },

    footerLabel: {
        fontWeight: "bold",
        width: "22%"
    }

};

// Printable Vendor Return slip. Same visual structure as GoodsRequestForm,
// scoped to a batch number instead of a request number, and driven off
// stock.vendor_status = 'Return Initiated'.
export default function VendorReturnForm({ batchNumber: batchNumberProp } = {}) {

    const params = useParams();
    const location = useLocation();
    const batchNumber = batchNumberProp || params.batchNumber;

    // mode=download or mode=print (set by the listing page's Download/Print
    // buttons) triggers the browser's print dialog automatically once data
    // has loaded. Plain navigation (Preview, or direct URL) leaves it alone.
    const mode = new URLSearchParams(location.search).get("mode");

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [unit1Location, setUnit1Location] = useState(null); // from Configuration > Location Master (Unit 1 / Regd. Office)

    // Return Date is not stored against the batch — it's always "today",
    // the day the slip is generated/printed. Returned By is always the
    // admin user for now (no multi-user attribution yet), so both are
    // fixed here rather than coming from the API response.
    const RETURNED_BY = "Admin";
    const returnDate = new Date().toLocaleDateString("en-GB"); // dd/mm/yyyy

    useEffect(() => {

        const fetchData = async () => {

            try {

                setLoading(true);
                setError("");

                const res = await axios.get(
                    `${VENDOR_API}/vendor-return/print/${encodeURIComponent(batchNumber)}`
                );

                setRows(res.data?.data || []);

            } catch (err) {

                console.log(err);
                setError("Failed to load vendor return data");

            } finally {

                setLoading(false);

            }

        };

        if (batchNumber) {
            fetchData();
        }

    }, [batchNumber]);

    useEffect(() => {
        const loadUnit1Location = async () => {
            try {
                const res = await axios.get(`${MASTER_API}/location-master`);
                const locations = Array.isArray(res.data) ? res.data : [];
                const unit1 = locations.find((loc) => (loc.location_label || "").toLowerCase().includes("1")) || null;
                setUnit1Location(unit1);
            } catch (err) {
                console.error("LOCATION MASTER FETCH ERROR", err);
                setUnit1Location(null);
            }
        };
        loadUnit1Location();
    }, []);

    // Unit 1 (Regd. Office) name/address, shown under the company name. Falls
    // back to the static text only if Location Master hasn't been set up yet.
    const unit1Name = unit1Location?.company_name || "SBEE CABLES (INDIA) LIMITED";
    const unit1AddressText = unit1Location
        ? `${unit1Location.address_line1}${unit1Location.address_line2 ? ", " + unit1Location.address_line2 : ""}, ${unit1Location.city}${unit1Location.pin_code ? " - " + unit1Location.pin_code : ""}, ${unit1Location.state}`
        : "No.282 & 283, KIADB Industrial Area, Phase 2, Harohalli, Kanakapura Taluk, Ramanagar - 562112, Karnataka, INDIA";

    // Auto-trigger print once the slip has actually rendered with data.
    useEffect(() => {
        if (!loading && !error && rows.length > 0 && (mode === "print" || mode === "download")) {
            const timer = setTimeout(() => window.print(), 300);
            return () => clearTimeout(timer);
        }
    }, [loading, error, rows, mode]);

    const first = rows[0] || {};

    const isEmbedded = !!batchNumberProp;

    const inner = (
        <>
            {!isEmbedded && (
                <div style={styles.toolbar} className="vr-no-print">
                    <button style={styles.printBtn} onClick={() => window.print()}>
                        Print
                    </button>
                </div>
            )}

            {loading && (
                <div style={styles.status}>Loading...</div>
            )}

            {error && (
                <div style={{ ...styles.status, ...styles.error }}>
                    {error}
                </div>
            )}

            {!loading && !error && rows.length === 0 && (
                <div style={styles.status}>
                    No vendor return found for batch {batchNumber}
                </div>
            )}

            {!loading && !error && rows.length > 0 && (
                <div style={styles.slip} id="vr-print-area">

                    <div style={styles.header}>
                        <img
                            src={sbeeLogo}
                            alt="SBEE Cables logo"
                            style={styles.logo}
                        />
                        <div style={styles.titleBlock}>
                            <div style={styles.title}>
                                {unit1Name}
                            </div>
                            <div style={styles.address}>
                                {unit1AddressText}
                            </div>
                            <div style={styles.subtitle}>
                                VENDOR RETURN NOTE
                            </div>
                        </div>
                    </div>

                    <table style={styles.table}>
                        <tbody>
                            <tr>
                                <td style={{ ...styles.metaTdFirstRow, ...styles.metaLabel }}>
                                    Batch No :
                                </td>
                                <td style={styles.metaTdFirstRow}>
                                    {first.batch_number || ""}
                                </td>
                                <td style={{ ...styles.metaTdFirstRow, ...styles.metaLabel }}>
                                    GRN No:
                                </td>
                                <td style={styles.metaTdFirstRow}>
                                    {first.grn_number || ""}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ ...styles.metaTd, ...styles.metaLabel }}>
                                    PO Number
                                </td>
                                <td style={styles.metaTd}>
                                    {first.po_number || ""}
                                </td>
                                <td style={{ ...styles.metaTd, ...styles.metaLabel }}>
                                    Vendor
                                </td>
                                <td style={styles.metaTd}>
                                    {first.vendor || ""}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ ...styles.metaTd, ...styles.metaLabel }}>
                                    GRN Date
                                </td>
                                <td style={styles.metaTd}>
                                    {first.grn_date || ""}
                                </td>
                                <td style={{ ...styles.metaTd, ...styles.metaLabel }}>
                                    Status
                                </td>
                                <td style={styles.metaTd}>
                                    {first.test_type ? `Rejected - ${first.test_type}` : "Rejected"}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div style={styles.instruction}>
                        Items Returned To Vendor
                    </div>

                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, ...styles.colSl }}>
                                    Sl No.
                                </th>
                                <th style={styles.th}>Product Code</th>
                                <th style={styles.th}>Description</th>
                                <th style={styles.th}>UOM</th>
                                <th style={styles.th}>Qty</th>
                                <th style={styles.th}>Comments</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, idx) => (
                                <tr key={r.id || idx}>
                                    <td style={styles.td}>{idx + 1}</td>
                                    <td style={styles.td}>
                                        {r.product_code}
                                    </td>
                                    <td style={styles.tdLeft}>
                                        {r.description}
                                    </td>
                                    <td style={styles.td}>{r.uom}</td>
                                    <td style={styles.td}>{r.qty}</td>
                                    <td style={styles.tdLeft}>{r.comments || ""}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <table style={styles.table}>
                        <tbody>
                            <tr>
                                <td
                                    style={{
                                        ...styles.footerTdFirstRow,
                                        ...styles.footerLabel
                                    }}
                                >
                                    Returned By
                                </td>
                                <td style={styles.footerTdFirstRow}>{RETURNED_BY}</td>
                                <td
                                    style={{
                                        ...styles.footerTdFirstRow,
                                        ...styles.footerLabel
                                    }}
                                >
                                    Return Date
                                </td>
                                <td style={styles.footerTdFirstRow}>{returnDate}</td>
                            </tr>
                            <tr>
                                <td
                                    style={{
                                        ...styles.footerTd,
                                        ...styles.footerLabel
                                    }}
                                >
                                    Vendor Acknowledged By
                                </td>
                                <td style={styles.footerTd}></td>
                                <td
                                    style={{
                                        ...styles.footerTd,
                                        ...styles.footerLabel
                                    }}
                                >
                                    Acknowledged Date
                                </td>
                                <td style={styles.footerTd}></td>
                            </tr>
                        </tbody>
                    </table>

                </div>
            )}

            <style>
                {`
                    @media print {
                        .vr-no-print {
                            display: none !important;
                        }
                        body {
                            background: #fff !important;
                        }
                        @page {
                            size: A4;
                            margin: 14mm;
                        }
                    }
                `}
            </style>
        </>
    );

    if (isEmbedded) {
        return (
            <div style={{ padding: "16px", overflowY: "auto", height: "100%" }}>
                {inner}
            </div>
        );
    }

    return (
        <div style={styles.wrapper}>
            {inner}
        </div>
    );

}